import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { submitFullSessionEvaluatorReport } from '../../services/api';
import useMitiSlotMachine from '../../hooks/useMitiSlotMachine';
import ContemplatingIndicator from '../chat/ContemplatingIndicator';
import SkillCodeNameTooltip from './SkillCodeNameTooltip';
import './ReportFullSessionAnalytics.css';
import '../dialogs/DialogBase.css';
import '../dialogs/FeedbackDialog.css';

const MITI_CODE_CLASS = {
  Q: 'miti-chip--Q', AF: 'miti-chip--AF', SR: 'miti-chip--SR', CR: 'miti-chip--CR',
  Seek: 'miti-chip--Seek', GI: 'miti-chip--GI', Persuade: 'miti-chip--Persuade',
  Persuade_with: 'miti-chip--Persuade_with', Emphasize: 'miti-chip--Emphasize',
  Confront: 'miti-chip--Confront', NC: 'miti-chip--NC',
};

const MITI_DISPLAY_NAMES = {
  Q: 'Question', AF: 'Affirmation', SR: 'Simple Reflection', CR: 'Complex Reflection',
  Seek: 'Seeking Collaboration', GI: 'Giving Information', Persuade: 'Persuade',
  Persuade_with: 'Persuade with Permission', Emphasize: 'Emphasize Autonomy',
  Confront: 'Confront', NC: 'Not Coded',
};

function FullSessionMitiChip({ label }) {
  const fullName = MITI_DISPLAY_NAMES[label] || label;
  const chip = (
    <span className={`miti-chip ${MITI_CODE_CLASS[label] || 'miti-chip--NC'}`} aria-label={fullName}>
      {label}
    </span>
  );
  return fullName === label ? chip : <SkillCodeNameTooltip name={fullName}>{chip}</SkillCodeNameTooltip>;
}

function ReportFullSessionAnalytics({
  t,
  theme = 'dark',
  title,
  subtitle,
  currentTurnMessages,
  displayStageMetrics,
  hasStageMetricsData,
  streamingActive = false,
  isViewingLatest = false,
  isClientMode = false,
  conversationStarted = false,
  showClientParameters = true,
  revealedItems,
  analyticsVisible = true,
  onToggleAnalytics,
  showVisibilityToggle = false,
  showRevealedItems = false,
  emptyScoringMessage,
  emptyMetricsMessage,
  AnalyticsMessageComponent,
  LoadingDotsComponent,
  onBackToMetrics,
  onShowAutomatedMiscMitiCounsellor,
  turnIndex = -1,
  guidanceVisible = false,
  onRevealGuidance,
  onHideGuidance,
  reportSessionId = null,
}) {
  const [feedbackReportOpen, setFeedbackReportOpen] = useState(false);
  const [feedbackReportText, setFeedbackReportText] = useState('');
  const [feedbackReportSubmitting, setFeedbackReportSubmitting] = useState(false);
  const [feedbackReportError, setFeedbackReportError] = useState('');
  const showStreamingState = streamingActive && isViewingLatest;
  const hasInteractionMessages = Boolean(
    (currentTurnMessages?.counsellor && currentTurnMessages.counsellor.length > 0) ||
    (currentTurnMessages?.client && currentTurnMessages.client.length > 0)
  );
  const automitiLabels = currentTurnMessages?.automitiLabels || [];
  const evaluation = currentTurnMessages?.evaluation || null;
  // Guidance coaches a human counsellor; turns taken by the LLM counsellor have none.
  const showGuidance = !currentTurnMessages?.counsellorIsLlm;
  const coachingLoading = isViewingLatest && (
    Boolean(currentTurnMessages?.evaluationLoading) || showStreamingState
  );
  const slotLabel = useMitiSlotMachine(coachingLoading && automitiLabels.length === 0);
  const concealGuidance = !guidanceVisible && Boolean(evaluation?.feedback);
  const canReportGuidance = Boolean(evaluation?.feedback) && guidanceVisible && !coachingLoading;
  const feedbackReportWordCount = feedbackReportText.trim()
    ? feedbackReportText.trim().split(/\s+/).filter(Boolean).length
    : 0;

  const closeFeedbackReport = () => {
    setFeedbackReportOpen(false);
    setFeedbackReportText('');
    setFeedbackReportSubmitting(false);
    setFeedbackReportError('');
  };

  const handleFeedbackReportSubmit = async () => {
    if (turnIndex < 0 || !feedbackReportText.trim() || feedbackReportSubmitting) return;
    setFeedbackReportSubmitting(true);
    setFeedbackReportError('');
    try {
      await submitFullSessionEvaluatorReport({
        sessionId: reportSessionId,
        turnIndex,
        reportText: feedbackReportText.trim(),
      });
      closeFeedbackReport();
    } catch (error) {
      setFeedbackReportError(
        error?.response?.data?.error || 'Failed to submit report. Please try again.',
      );
      setFeedbackReportSubmitting(false);
    }
  };

  return (
    <div className={`in-session-analytics oars-analytics-content${onBackToMetrics ? ' post-session-review' : ''}`}>
      <div className="analytics-header">
        <div className="analytics-header-row">
          <div className="analytics-header-copy">
            <h4 className="analytics-title">{title}</h4>
            {subtitle ? <p className="analytics-subtitle">{subtitle}</p> : null}
          </div>
          {showVisibilityToggle && onToggleAnalytics && (
            <div className="analytics-header-right">
              <button
                type="button"
                className="analytics-toggle-chevron"
                onClick={onToggleAnalytics}
                aria-expanded={analyticsVisible}
                aria-label={analyticsVisible ? t('report_hide_analytics_aria') : t('report_show_analytics_aria')}
                title={analyticsVisible ? t('report_hide_analytics_aria') : t('report_show_analytics_aria')}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                  {analyticsVisible ? <polyline points="6 9 12 15 18 9" /> : <polyline points="6 15 12 9 18 15" />}
                </svg>
              </button>
            </div>
          )}
        </div>
        {analyticsVisible && <div className="analytics-header-divider" aria-hidden="true" />}
      </div>

      {analyticsVisible ? (
        <div className="analytics-scroll-body">
          <div className="analytics-sections">
            {!isClientMode ? (
              <>
                <div className="analytics-card">
                  <div className="analytics-card-header">
                    <h5 className="analytics-card-title">Skill Used</h5>
                    {onShowAutomatedMiscMitiCounsellor && (
                      <button
                        type="button"
                        className="analytics-toggle-icon"
                        onClick={onShowAutomatedMiscMitiCounsellor}
                        aria-label="Open counsellor coding manual"
                        title="Open counsellor coding manual"
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15.5A2.5 2.5 0 0 0 17.5 16H4z" />
                          <path d="M6.5 3v13" />
                          <path d="M8.5 7h8" />
                          <path d="M8.5 10.5h8" />
                          <path d="M8.5 14h5" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="analytics-card-content">
                    {coachingLoading && automitiLabels.length === 0 ? (
                      <div className="oars-attempt-entry miti-slot-machine" aria-label="Analyzing skill code">
                        <div className="oars-label-row" aria-hidden="true">
                          <FullSessionMitiChip key={slotLabel} label={slotLabel} />
                        </div>
                      </div>
                    ) : (
                      <div className="oars-attempt-entry">
                        <div className="oars-label-row">
                          {(automitiLabels.length ? automitiLabels : ['NC']).map((label) => (
                            <FullSessionMitiChip key={label} label={label} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {showGuidance && (
                <div className="analytics-card strategy-guidance-card full-session-guidance-card">
                  <div className="analytics-card-header">
                    <h5 className="analytics-card-title">Guidance</h5>
                    <div className={`strategy-guidance-card__actions${concealGuidance ? ' strategy-guidance-card__actions--concealed' : ''}`}>
                      {guidanceVisible && evaluation?.feedback && (
                        <button
                          type="button"
                          className="analytics-toggle-icon"
                          onClick={onHideGuidance}
                          aria-label="Hide guidance"
                          title="Hide guidance"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                            <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
                            <circle cx="12" cy="12" r="2.5" />
                            <path d="m4 4 16 16" />
                          </svg>
                        </button>
                      )}
                      <button
                        type="button"
                        className="analytics-toggle-icon feedback-report-icon"
                        onClick={() => setFeedbackReportOpen(true)}
                        aria-label="Report incorrect feedback"
                        title={canReportGuidance ? 'Report incorrect feedback' : 'Available when guidance is ready and visible'}
                        disabled={!canReportGuidance}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M4 21V5" />
                          <path d="M4 5h10l-1.4 3L18 11H4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div
                    className={`analytics-card-content${concealGuidance ? ' strategy-guidance-card__content--concealed' : ''}`}
                    aria-hidden={concealGuidance ? 'true' : undefined}
                  >
                    {evaluation?.feedback ? (
                      <p className={`sr-feedback-text${coachingLoading ? ' specific-skill-guidance-streaming' : ''}`}>
                        {evaluation.feedback}
                      </p>
                    ) : currentTurnMessages?.guidanceError ? (
                      <p className="analytics-empty">{currentTurnMessages.guidanceError}</p>
                    ) : (
                      <p className="analytics-empty">
                        {coachingLoading ? <ContemplatingIndicator /> : emptyScoringMessage}
                      </p>
                    )}
                  </div>
                  {concealGuidance && (
                    <div className="strategy-guidance-card__mask">
                      <div className="strategy-guidance-card__mask-content">
                        <strong>Guidance available</strong>
                        <span>Review it now, or continue without viewing it.</span>
                        <button
                          type="button"
                          className="strategy-guidance-card__reveal-btn"
                          onClick={onRevealGuidance}
                        >
                          View Guidance
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </>
            ) : (
            <div className="analytics-card">
              <div className="analytics-card-header">
                <h5 className="analytics-card-title">{t('report_card_interaction_log')}</h5>
              </div>
              <div className="analytics-card-content">
                {showStreamingState ? (
                  <p className="analytics-empty"><LoadingDotsComponent /></p>
                ) : hasInteractionMessages ? (
                  <>
                    {currentTurnMessages?.counsellor?.map((msg, idx) => (
                      <AnalyticsMessageComponent key={`c-${idx}`} msg={msg} />
                    ))}
                    {currentTurnMessages?.client?.map((msg, idx) => (
                      <div key={`cl-${idx}`} className="analytics-message">
                        <span className="analytics-bullet">•</span>
                        <span className="analytics-text">{msg}</span>
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="analytics-empty">{t('report_no_data_yet')}</p>
                )}
              </div>
            </div>
            )}

            {conversationStarted && showClientParameters && displayStageMetrics?.client_type !== 'Profile-Reveal' && (
              <div className="analytics-card stage-metrics-card">
                <div className="analytics-card-header">
                  <h5 className="analytics-card-title">{t('report_client_parameters_title')}</h5>
                </div>
                {showStreamingState ? (
                  <div className="analytics-card-content">
                    <p className="analytics-empty"><LoadingDotsComponent /></p>
                  </div>
                ) : hasStageMetricsData ? (
                  <div className="stage-metrics-grid">
                    {displayStageMetrics?.stage !== null && (
                      <div className="metric-item">
                        <span className="metric-label">{t('report_metric_stage_label')}</span>
                        <span className="metric-value">{displayStageMetrics.stage}</span>
                      </div>
                    )}
                    {displayStageMetrics?.change_score !== null && displayStageMetrics?.change_score !== displayStageMetrics?.change_score_floor && (
                      <div className="metric-item">
                        <span className="metric-label">{t('report_metric_change_score_label')}</span>
                        <span className="metric-value">{displayStageMetrics.change_score}</span>
                      </div>
                    )}
                    {displayStageMetrics?.change_score_floor !== null && (
                      <div className="metric-item">
                        <span className="metric-label">{t('report_metric_floor_label')}</span>
                        <span className="metric-value">{displayStageMetrics.change_score_floor}</span>
                      </div>
                    )}
                    {displayStageMetrics?.refusal_count !== null && (
                      <div className="metric-item">
                        <span className="metric-label">{t('report_metric_refusals_label')}</span>
                        <span className="metric-value">
                          {displayStageMetrics?.client_type === 'profile'
                            ? t('report_refusals_not_tracked')
                            : `${displayStageMetrics.refusal_count}/3`}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="analytics-card-content">
                    <p className="analytics-empty">{emptyMetricsMessage}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {showRevealedItems && (revealedItems?.motivations?.length > 0 || revealedItems?.barriers?.length > 0 || revealedItems?.next_steps?.length > 0) && (
            <div className="revealed-items-section">
              <h4 className="revealed-items-title">{t('report_cumulative_topics_title')}</h4>

              {revealedItems.motivations.length > 0 && (
                <div className="revealed-category motivations">
                  <h5 className="revealed-category-title">{t('report_motivations_title')}</h5>
                  <ul className="revealed-list">
                    {revealedItems.motivations.map((item, idx) => (
                      <li key={idx} className="revealed-item">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {revealedItems.barriers.length > 0 && (
                <div className="revealed-category barriers">
                  <h5 className="revealed-category-title">{t('report_barriers_title')}</h5>
                  <ul className="revealed-list">
                    {revealedItems.barriers.map((item, idx) => (
                      <li key={idx} className="revealed-item">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {revealedItems.next_steps.length > 0 && (
                <div className="revealed-category">
                  <h5 className="revealed-category-title">{t('report_next_steps_title')}</h5>
                  <ul className="revealed-list">
                    {revealedItems.next_steps.map((item, idx) => (
                      <li key={idx} className="revealed-item">{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
      {feedbackReportOpen && typeof document !== 'undefined' && createPortal(
        <div className={`full-session-feedback-portal App${theme === 'dark' ? ' dark' : ''}`}>
          <div className="app-confirm-overlay app-feedback-overlay" role="dialog" aria-modal="true">
            <div className="app-confirm-panel app-feedback-panel" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="app-feedback-close"
              onClick={closeFeedbackReport}
              aria-label="Close report dialog"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
            <div className="app-dialog-head">
              <h4 className="app-dialog-title">Think Our Evaluator is Incorrect?</h4>
            </div>
            <div className="app-confirm-content">
              <p>If something in this evaluation seems off, you can flag it here so we can review it more closely.</p>
            </div>
            <div className="oars-feedback-context-card">
              <div className="app-feedback-field">
                <p className="app-feedback-question-text">Evaluated Response</p>
                <div className="message-text message-text--user oars-report-response-box">
                  <div className="oars-report-response-scroll">
                    <p className="oars-report-text">{currentTurnMessages?.traineeText || '—'}</p>
                  </div>
                </div>
              </div>
              <div className="app-feedback-field">
                <p className="app-feedback-question-text">Evaluator Feedback</p>
                <div className="analytics-card oars-report-feedback-card">
                  <div className="analytics-card-content oars-report-feedback-content">
                    <p className="sr-feedback-text">{evaluation?.feedback || '—'}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="app-feedback-field">
              <div className="app-feedback-field-header">
                <label className="app-feedback-label" htmlFor="full-session-report-feedback-input">Your Feedback</label>
                <span className="app-feedback-word-count">{`${feedbackReportWordCount} / 150 words`}</span>
              </div>
              <textarea
                id="full-session-report-feedback-input"
                className="app-feedback-input"
                value={feedbackReportText}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  const nextCount = nextValue.trim()
                    ? nextValue.trim().split(/\s+/).filter(Boolean).length
                    : 0;
                  if (nextCount <= 150) setFeedbackReportText(nextValue);
                }}
                rows={4}
                placeholder="Share your thoughts here..."
              />
              {feedbackReportError && <p className="specific-strategies-error">{feedbackReportError}</p>}
            </div>
            <div className="app-dialog-actions app-feedback-actions">
              <button
                type="button"
                className="app-confirm-btn primary"
                onClick={handleFeedbackReportSubmit}
                disabled={feedbackReportSubmitting || !feedbackReportText.trim()}
              >
                {feedbackReportSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default ReportFullSessionAnalytics;
